import { CommonStudent, CommonSkillField, CommonChapter } from '@codidae/common-types';
import { useMutation, useQuery } from '@tanstack/react-query';
import AltusTalk from 'components/altus-talk/altus-talk';
import Button from 'components/button/button';
import Flex from 'components/flex/flex';
import InputText from 'components/input-text/input-text';
import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { MdDelete, MdEditSquare, MdMenu, MdSend } from 'react-icons/md';
import { deleteChatbotRequest } from 'services/evaluations';
import { useStatusBarHeight } from 'hooks/use-status-bar-height';
import { useChatbot, UiState } from 'hooks/use-chatbot';
import { ChildrenDashboardTab } from 'views/children/dashboard';
import Select from 'components/select/select';
import { getChapters } from 'services/chapters';
import Checkbox from 'components/checkbox/checkbox';
import styles from './chatbot.module.scss';

/* eslint-disable-next-line */
export interface ChatbotProps {
  student: CommonStudent;
  chatbotContext: any;
  setChatbotContext: React.Dispatch<React.SetStateAction<any>>;
  setCurrentTab?: (tab: ChildrenDashboardTab) => void;
}

// Fonction vide pour éviter les avertissements ESLint
const noop = () => { /* Ne fait rien intentionnellement */ };

export function Chatbot({ student, chatbotContext, setChatbotContext, setCurrentTab }: ChatbotProps) {
  // // <<< AJOUT DEBUG : Vérifier la prop student au rendu >>>
  // console.log('[Chatbot Render] Received student prop:', JSON.stringify(student));
  // console.log(`[Chatbot Render] student.id value: ${student?.id}`);
  // // <<< FIN DEBUG >>>

  const height = useStatusBarHeight();
  const [currentMessage, setCurrentMessage] = useState('');
  const windowRef = useRef<HTMLDivElement>(null);
  const [requestId, setRequestId] = useState<number | undefined>(undefined);
  const [menu, setMenu] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [localSelectedChapterIds, setLocalSelectedChapterIds] = useState<number[]>([]);
  // --- State for Date Input ---
  const [localDateValue, setLocalDateValue] = useState<string>(''); 

  // Callback pour mettre à jour l'ID de requête (utilisé par useChatbot)
  const handleRequestCreated = useCallback((id: number) => {
    console.log('[Chatbot] ID de requête créée/mise à jour:', id);
    setRequestId(id);
  }, []);

  // Utiliser le hook useChatbot pour gérer les conversations
  const {
    messages,
    isLoading: isSendingMessage,
    loadingState,
    currentRequest,
    uiState,
    sendMessage,
    refetchRequests,
    requests,
  } = useChatbot(student.id, requestId, handleRequestCreated);

  // Chapitres (fetch tous)
  const { data: allChapters, isLoading: isLoadingChapters } = useQuery<CommonChapter[]>({ 
        queryKey: ['chapters'],
        queryFn: getChapters,
    staleTime: 3600000, // Cache 1h
  });

  // Déplacer le useMemo ici, au niveau du composant
  const chaptersByParent = useMemo(() => {
    console.log('[Chatbot] Recomputing chaptersByParent');
    const grouped: { [key: number]: CommonChapter[] } = {};
    const parents: CommonChapter[] = [];
    const evaluation = currentRequest?.evaluation;
    if (!allChapters || !evaluation?.field?.id || !student?.cycle?.id) {
      return { parents: [], children: {} };
    }
    const studentCycleId = student.cycle.id;
    const fieldId = evaluation.field.id;

    allChapters.forEach(chapter => {
      if (chapter.field.id === fieldId && chapter.parent === null && chapter.cycles?.some(cy => cy.id === studentCycleId)) {
        parents.push(chapter);
        grouped[chapter.id] = [];
      }
    });
    allChapters.forEach(chapter => {
      if (chapter.parent && Object.prototype.hasOwnProperty.call(grouped, chapter.parent.id) && chapter.field.id === fieldId && chapter.cycles?.some(cy => cy.id === studentCycleId)) {
        grouped[chapter.parent.id].push(chapter);
      }
    });
    parents.sort((a, b) => a.name.localeCompare(b.name));
    Object.values(grouped).forEach(childrenList => childrenList.sort((a, b) => a.name.localeCompare(b.name)));
    return { parents, children: grouped };
  }, [allChapters, currentRequest?.evaluation, student?.cycle?.id]);

  // Effet simplifié pour gérer UNIQUEMENT le contexte entrant
  useEffect(() => {
    console.log('[Chatbot] useEffect CONTEXT CHECK - Start', {
      currentInternalRequestId: requestId, // Afficher l'état interne actuel
      contextReceived: chatbotContext ? JSON.stringify(chatbotContext) : 'null',
    });

    if (chatbotContext) {
      const contextRequestId = chatbotContext.requestId; // Extraire l'ID du contexte
      console.log('[Chatbot] useEffect CONTEXT CHECK - Contexte reçu', { contextRequestId });

      // Mettre à jour l'état interne UNIQUEMENT si différent
      if (contextRequestId !== requestId) { 
        console.log(`[Chatbot] useEffect CONTEXT CHECK - Mise à jour de l'état requestId de ${requestId} vers ${contextRequestId}`);
        setRequestId(contextRequestId);
        // Le hook useChatbot (qui dépend de requestId) se chargera de lancer la conversation
      }
      
      // Consommer le contexte une fois traité
      console.log('[Chatbot] useEffect CONTEXT CHECK - Consommation du contexte.');
      setChatbotContext(null);
    } else {
      console.log('[Chatbot] useEffect CONTEXT CHECK - Aucun contexte reçu ou déjà consommé.');
    }

    console.log('[Chatbot] useEffect CONTEXT CHECK - End');
    // Dépendances : seulement chatbotContext et setChatbotContext (et requestId pour la comparaison)
  }, [chatbotContext, setChatbotContext, requestId]);
  
  // Effet pour le défilement (séparé)
  useEffect(() => {
      setTimeout(() => {
      windowRef.current?.scrollTo({ top: windowRef.current?.scrollHeight, behavior: 'smooth' });
    }, 100); 
    // Déclencher quand les messages changent ou l'état de chargement
  }, [messages, isSendingMessage]);

  const deleteRequestMutation = useMutation({
    mutationFn: deleteChatbotRequest,
    onSuccess(data, variables, context) {
      console.log('[Chatbot] deleteRequestMutation Success - ID:', variables);
      refetchRequests();
      if (variables === requestId) {
          handleNewChat();
      }
    },
    onError: (error) => {
        console.error("[Chatbot] Erreur suppression requête:", error);
    }
  });

  const handleSimpleMessageSend = useCallback((messageContent: string) => {
    if (!messageContent.trim() && messages.length > 0) return; // Ne pas envoyer vide sauf si c'est le début
    console.log(`[Chatbot] Sending simple message: "${messageContent}"`);
    
    // RETRAIT: L'affichage sera géré par le hook useChatbot après réponse du backend
    // if (messageContent.trim()) {
    //   const userMessage = { role: 'user' as const, content: messageContent };
    //   setMessages(prev => [...prev, userMessage]);
    // }
    
    // Envoyer au backend
    sendMessage({ message: messageContent, childrenId: student.id });
    
    // Vider l'input
    setCurrentMessage('');
  }, [sendMessage, student.id, messages.length]);

  const handleSelectField = useCallback((field: CommonSkillField) => {
    if (!field || isSendingMessage) return;
    handleSimpleMessageSend(`Je choisis la matière ${field.name}.`);
  }, [handleSimpleMessageSend, isSendingMessage]);

  const handleSetDate = useCallback((date: string | null) => {
    if (isSendingMessage) return;
    let message = '';
    if (date === null) {
      message = "sans date"; // Message plus simple
      setDateError(null);
    } else {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(localDateValue); 
      if (selectedDate >= today) {
        message = `date : ${selectedDate.toLocaleDateString('fr-FR')}`;
        setDateError(null);
      } else {
        setDateError('La date ne peut pas être dans le passé.');
        return;
      }
    }
    handleSimpleMessageSend(message);
  }, [isSendingMessage, localDateValue, handleSimpleMessageSend]);

  const handleSetChapters = useCallback(() => {
    let message = '';
    if (localSelectedChapterIds.length === 0) {
      message = 'aucun chapitre'; // Message simple si aucun chapitre
    } else {
      // Construire le message pour l'historique (optionnel mais utile)
      const chapterNames = localSelectedChapterIds
        .map(id => allChapters?.find(c => c.id === id)?.name)
        .filter(Boolean);
      message = `chapitres : ${chapterNames.join(', ')}`;
    }

    console.log(`[Chatbot] Sending chapters. Message: "${message}", IDs:`, localSelectedChapterIds);
    const childrenId = student.id; // Capture stable value
    const chapterIds = localSelectedChapterIds; // Capture stable value
    // eslint-disable-next-line object-shorthand
    sendMessage({
      message, 
      childrenId, 
      selectedChapterIds: chapterIds 
    });
  }, [localSelectedChapterIds, allChapters, student.id, sendMessage]);

  const handleConfirm = useCallback(() => {
    handleSimpleMessageSend("Oui");
    refetchRequests(); // Garder ? Utile pour mettre à jour le menu
  }, [handleSimpleMessageSend, refetchRequests]);

  const handleCancel = useCallback(() => {
    handleSimpleMessageSend("Non");
  }, [handleSimpleMessageSend]);

  const handleNewChat = () => {
    console.log('[Chatbot] handleNewChat - Resetting requestId to undefined');
    setRequestId(undefined);
    setMenu(false);
    // Reset local state?
    setCurrentMessage('');
    setLocalDateValue('');
    setLocalSelectedChapterIds([]);
    setDateError(null);
    // Le hook useChatbot devrait reset uiState quand requestId devient undefined
  };

  // Effet pour synchroniser localDateValue
  useEffect(() => {
    const evaluationDate = currentRequest?.evaluation?.date;
    const newLocalDate = evaluationDate ? new Date(evaluationDate).toISOString().split('T')[0] : '';
    console.log(`[Chatbot Date Sync] Backend date changed to: ${newLocalDate}. Updating local state.`);
    // Met à jour directement l'état local lorsque la date du backend change.
    // Pas besoin de comparer avec localDateValue ici, ce qui satisfait exhaustive-deps.
    setLocalDateValue(newLocalDate);
  // Ne dépend QUE de la date du backend. 
  }, [currentRequest?.evaluation?.date]);

  // Effet pour synchroniser les chapitres locaux
  useEffect(() => {
    console.log(`[Chatbot Sync Effect] Triggered by change in requestId (${requestId}) or backend chapters.`);

    // Récupère les IDs du backend directement
    const backendChapterIds = currentRequest?.evaluation?.chapters?.map((c: any) => c.id) ?? [];
    console.log('[Chatbot Sync Effect] Backend chapter IDs for current request:', backendChapterIds);

    // Met à jour l'état local directement lorsque les données du backend changent.
    // Pas besoin de comparer avec l'état local, ce qui satisfait exhaustive-deps.
    console.log(`[Chatbot Sync Effect] Updating local chapters to backend state:`, backendChapterIds);
    setLocalSelectedChapterIds(backendChapterIds);

  // Ne dépend que de l'ID de la requête et des chapitres du backend.
  }, [requestId, currentRequest?.evaluation?.chapters]);

  // --- AJOUT : Handler pour le nouveau bouton header ---
  const handleGoToEvaluation = () => {
    if (setCurrentTab && currentRequest?.evaluation?.id) {
      sessionStorage.setItem('highlightEvalId', String(currentRequest.evaluation.id));
      console.log(`[Chatbot] Storing eval ID ${currentRequest.evaluation.id} for highlight (header button).`);
      setCurrentTab('home');
    } else {
      console.warn('[Chatbot] Cannot navigate home: setCurrentTab or evaluation ID missing.');
    }
  };
  // --- FIN AJOUT ---

  const renderFieldSelector = useCallback(() => (
    <Flex direction="column" gap={10} width="100%" style={{ marginTop: 15, alignSelf: 'flex-start' }}>
      {/* CORRECTION: Utiliser student.fields || [] pour éviter undefined */} 
      <Select
        options={[
          { value: '', label: 'Sélectionner une matière' },
          ...(student?.fields || []).map(f => ({ value: String(f.id), label: f.name }))
        ]}
        onChange={(value) => {
          if (value) {
            const selectedField = student?.fields?.find(f => String(f.id) === value);
            if (selectedField) handleSelectField(selectedField);
          }
        }}
        labeled
      />
    </Flex>
  ), [student?.fields, handleSelectField]);

  const renderDateSelector = useCallback(() => (
    <Flex direction="column" gap={15} width="100%" align="center" style={{ marginTop: 15, alignSelf: 'flex-start' }}>
      <Flex direction="row" width="100%" gap={10} align="flex-end">
        <InputText
          placeholder="Date (AAAA-MM-JJ)" type="date" labeled
          onChange={(value) => { setLocalDateValue(value); setDateError(null); }}
          value={localDateValue}
        />
        <Button
          onClick={() => {
            if (localDateValue) {
              handleSetDate(localDateValue);
            } else {
              setDateError('Veuillez sélectionner une date.');
            }
          }}
          disabled={isSendingMessage || !localDateValue}
        >Valider</Button>
      </Flex>
      {dateError && <p style={{ color: 'red', fontSize: 'small', margin: 0, width: '100%' }}>{dateError}</p>}
      <p style={{color: '#666', fontSize: 'small', margin: '5px 0 0 0', width: '100%'}}>Ou tape &quot;sans date&quot; dans la zone de message.</p>
    </Flex>
  ), [localDateValue, dateError, isSendingMessage, handleSetDate]);

  const renderChapterSelector = useCallback(() => {
    console.log('[Chatbot] Rendering chapter selector');
    return (
      <Flex direction="column" gap={15} width="100%" style={{ marginTop: 15, alignSelf: 'flex-start' }}>
        {isLoadingChapters && <p>Chargement...</p>}
        {!isLoadingChapters && chaptersByParent.parents.length === 0 && <p>Aucun chapitre disponible.</p>}
        {!isLoadingChapters && chaptersByParent.parents.map(parentChapter => (
          <div key={`parent-${parentChapter.id}`} style={{ marginBottom: '10px' }}>
            <strong style={{ display: 'block', marginBottom: '5px', borderBottom: '1px solid #eee', paddingBottom: '3px' }}>{parentChapter.name}</strong>
            {(chaptersByParent.children[parentChapter.id]?.length > 0) ? (
              chaptersByParent.children[parentChapter.id].map(subChapter => (
                <div key={subChapter.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '5px 0 5px 10px' }} 
                     onClick={() => setLocalSelectedChapterIds(prev => 
                        prev.includes(subChapter.id) ? prev.filter(id => id !== subChapter.id) : [...prev, subChapter.id])}>
                  <Checkbox 
                    checked={localSelectedChapterIds.includes(subChapter.id)} 
                    onChange={() => noop()} 
                  /> 
                  <span>{subChapter.name}</span> 
                </div>
              ))
            ) : <p style={{ fontStyle: 'italic', color: '#888', paddingLeft: '10px', fontSize: 'small' }}>Aucun sous-chapitre.</p>}
          </div>
        ))}
        {!isLoadingChapters && chaptersByParent.parents.length > 0 && (
          <Button onClick={handleSetChapters} disabled={isSendingMessage} style={{ width: '100%', marginTop: 10 }}>Valider les chapitres</Button>
        )}
      </Flex>
    );
  }, [chaptersByParent, isLoadingChapters, localSelectedChapterIds, isSendingMessage, handleSetChapters]);

  const renderConfirmationButtons = useCallback(() => (
      <Flex direction="row" gap={10} width="100%" justify="center" style={{ marginTop: 15 }}>
          <Button onClick={handleConfirm} disabled={isSendingMessage} color="primary">Oui</Button>
          <Button onClick={handleCancel} disabled={isSendingMessage} color="dark" outline>Non</Button>
      </Flex>
  ), [handleConfirm, handleCancel, isSendingMessage]);

  const renderPostCompletionSuggestions = useCallback(() => (
      <Flex direction="column" gap={10} width="100%" style={{ marginTop: 20, alignSelf: 'flex-start' }}>
          <AltusTalk type="question" variant="small" smallWidth={40}>Que veux-tu faire maintenant ?</AltusTalk>
          <div className={styles.postCompletionSuggestionsContainer}>
              {[
                  { text: "Quand est l'évaluation ?", value: "Quand est l'évaluation ?" },
                  { text: "Quels sont les chapitres ?", value: "Quels sont les chapitres de cette évaluation ?" },
                  { text: "Je veux changer la date.", value: "Je veux changer la date." },
                  { text: "Je veux changer les chapitres.", value: "Je veux changer les chapitres." },
              ].map((suggestion) => (
                  <div 
                      key={suggestion.value} 
                      onClick={() => {
                          setCurrentMessage(suggestion.value);
                      }}
                      className={styles.suggestionChip}
                  >
                      {suggestion.text}
                  </div>
              ))}
          </div>
      </Flex>
  ), []);

  const renderInitialPrompt = useCallback(() => (
     <Flex direction="column" width="100%" gap={10} align="center" style={{ marginTop: 20 }}>
         <Button onClick={() => handleSimpleMessageSend('')} style={{ width: '100%' }}>Créer une évaluation</Button>
         <Button onClick={() => { console.log("Discuter cliqué"); handleSimpleMessageSend("Discuter"); }} style={{ width: '100%' }}>Discuter</Button>
     </Flex>
  ), [handleSimpleMessageSend]);

  const renderInputArea = () => {
    const showInput = uiState !== UiState.INITIAL_PROMPT || messages.length > 0;
    if (!showInput) return null;

    const tabBarHeight = 80;
      return (
      <Flex direction="column" align="center" width="100%" gap={10} position="fixed" bottom={`${tabBarHeight}px`} left="0" right="0" style={{ padding: '15px 20px', background: 'white', boxShadow: '0px -2px 10px rgba(0,0,0,0.1)', zIndex: 10 }}>
           <InputText 
             placeholder="Message Altus"
             value={currentMessage}
             multiline
             onChange={(e) => setCurrentMessage(e)}
             disabled={isSendingMessage} 
           />
         <Button style={{ position: 'absolute', bottom: 27, right: 25, width: 40 }} 
             disabled={!currentMessage || isSendingMessage}
                 onClick={() => { handleSimpleMessageSend(currentMessage); setCurrentMessage(''); }}>
             <MdSend />
           </Button>
       </Flex>
    );
  }

  const renderMainContent = () => {
    console.log(`[Chatbot][renderMainContent] Current uiState: ${uiState}`);

    return (
      <Flex
        direction="column"
        align={messages.length === 0 && uiState === UiState.INITIAL_PROMPT ? 'center' : 'flex-start'}
        width="100%"
        ref={windowRef}
        gap={10}
        style={{
          flexGrow: 1,
          overflowY: 'auto',
          paddingBottom: 160,
          paddingTop: currentRequest?.isCompleted ? '70px' : '20px',
          paddingLeft: 20,
          paddingRight: 20,
          justifyContent: messages.length === 0 && uiState === UiState.INITIAL_PROMPT ? 'center' : 'flex-start'
        }}
      >
        {/* Affichage Message Initial */} 
        {messages.length === 0 && uiState === UiState.INITIAL_PROMPT && (
             <AltusTalk style={{ width: '100%', textAlign: 'center', marginTop: 20 }}>Bonjour {student.firstname} ! Que veux-tu faire ?</AltusTalk>
        )}

        {/* Affichage des messages */} 
        {messages.map((message, index) => (
           <div key={`${currentRequest?.id || 'conv'}-${index}-${message.role}`} 
                style={{ width: '100%', alignSelf: message.role === 'assistant' ? 'flex-start' : 'flex-end', display: 'flex', justifyContent: message.role === 'assistant' ? 'flex-start' : 'flex-end' }}>
             <AltusTalk smallWidth={40} variant={message.role === 'assistant' ? 'small' : 'small-reverse'} type={message.role === 'assistant' ? 'hi' : 'question'} style={{ justifyContent: message.role === 'assistant' ? 'left' : 'flex-end', width: '80%' }} noMove>
               {message.content}
              </AltusTalk>
            </div>
         ))}

        {/* Affichage conditionnel basé sur uiState */} 
        { !isSendingMessage && (
            <> 
              {/* Afficher uniquement si PAS en mode discussion libre */} 
              {uiState !== UiState.FREE_DISCUSSION_MODE && (
                  <> 
                      {uiState === UiState.INITIAL_PROMPT && messages.length === 0 && renderInitialPrompt()} 
                      {uiState === UiState.AWAITING_FIELD && renderFieldSelector()} 
                      {uiState === UiState.AWAITING_DATE && renderDateSelector()} 
                      {uiState === UiState.AWAITING_CHAPTERS && renderChapterSelector()} 
                      {uiState === UiState.AWAITING_CONFIRMATION && renderConfirmationButtons()} 
                      {(uiState === UiState.COMPLETED_IDLE || uiState === UiState.SHOWING_MESSAGE) && renderPostCompletionSuggestions()} 
                  </>
              )}
              {/* Gérer l'état ERROR (toujours afficher) */} 
              {uiState === UiState.ERROR && (
                <AltusTalk type="sad" style={{marginTop: 15}}>Oups ! Une erreur est survenue...</AltusTalk>
              )}
              {/* Afficher un message spécifique si en mode discussion libre ? Optionnel */}
              {/* {uiState === UiState.FREE_DISCUSSION_MODE && (
                  <AltusTalk type="idea" style={{marginTop: 15}}>Mode discussion activé !</AltusTalk>
              )} */}
            </>
        )}

        {/* Indicateur de chargement */} 
        {isSendingMessage && (
           <div style={{ width: '100%', alignSelf: 'flex-start', display: 'flex', justifyContent: 'flex-start', marginTop: 10 }}>
          <AltusTalk variant="small" type="cloud" style={{ width: 'auto' }} smallWidth={40}>
               {loadingState?.startsWith('retry_countdown_') ? `... (${loadingState.split('_').pop()}s)` : "Réflexion..."}
          </AltusTalk>
          </div>
        )}
        
      </Flex>
    );
  };

  return (
    <Flex
      direction="column"
      justify="space-between"
      align="center"
      width="100%"
      height="100%"
      position="relative"
      style={{
        paddingBottom: 100,
      }}
    >

      <Flex 
        direction="row" 
        align="center" 
        width="100%" 
        justify="space-between" 
        className={styles.header} 
        style={{ paddingLeft: 20, paddingRight: 20 }}
      >
        <MdMenu onClick={() => setMenu(!menu)} style={{ cursor: 'pointer', fontSize: '1.5rem' }}/>
        <span style={{ fontWeight: 'bold', color: '#555', fontSize: '0.9rem' }}>
          {uiState === UiState.FREE_DISCUSSION_MODE ? 'Mode Discussion' : 'Mode Évaluation'}
        </span>
        <Flex align="center" gap={10}> 
        <MdEditSquare 
            onClick={handleNewChat} 
            style={{ cursor: 'pointer', fontSize: '1.5rem' }}
            title="Nouvelle conversation"
        /> 
      </Flex>
      </Flex>

      {currentRequest?.isCompleted && (
        <div 
          style={{
            position: 'absolute',
            top: '55px',
            left: 0,
            right: 0,
            zIndex: 5,
            padding: '10px 20px',
            textAlign: 'center',
            background: 'transparent'
          }}
        >
          <Button
            onClick={handleGoToEvaluation}
            color="primary"
            outline
          >
            Voir mon évaluation
          </Button>
        </div>
      )}

      {renderMainContent()}

      {renderInputArea()}

      <div className={`${styles.menuWrapper} ${menu ? styles.open : ''}`} onClick={() => setMenu(false)}>
        <div className={styles.menu} onClick={(e) => e.stopPropagation()} style={{ paddingTop: height + 15 }}>
          <Flex direction="row" align="center" justify="space-between" className={styles.header}>
            <MdMenu onClick={() => setMenu(false)} style={{ cursor: 'pointer'}}/>
          </Flex>
          <Flex direction="column" align="center" gap={10}>
            {Array.isArray(requests) && requests
               .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
               .map((request) => {
                 // Logique pour formater le nom de la conversation
                 const creationDate = new Date(request.createdAt).toLocaleDateString('fr-FR');
                 let buttonLabel = `Conv. du ${creationDate}`;

                 if (request.isFreeChatMode) {
                   buttonLabel = `Discussion du ${creationDate}`;
                 } else if (request.evaluation) {
                   const evalDate = request.evaluation.date ? new Date(request.evaluation.date).toLocaleDateString('fr-FR') : null;
                   const fieldName = request.evaluation.field?.name;

                   if (evalDate) {
                     buttonLabel = `Éval pour le ${evalDate}`;
                     if (fieldName) {
                       buttonLabel += ` (${fieldName})`;
                     }
                   } else if (fieldName) {
                     buttonLabel += ` - éval de ${fieldName}`;
                   }
                 }

                 return (
                 <Flex direction="row" align="center" key={request.id.toString()} gap={10} width="100%">
                  <Button
                    color="dark"
                      outline={request.id === requestId} 
                    onClick={() => {
                      console.log('Chatbot - Sélection de conversation depuis menu:', request.id);
                      setRequestId(request.id);
                       setMenu(false);
                    }}
                       style={{ width: '100%', textAlign: 'left' }} // Ajout textAlign
                  >
                        {buttonLabel} 
                  </Button>
                  <Button
                    color="dark"
                    onClick={() => {
                      console.log('Chatbot - Suppression depuis menu:', request.id);
                      deleteRequestMutation.mutate(request.id);
                     }}
                     style={{ width: 40 }}
                     disabled={deleteRequestMutation.isPending}
                  >
                    <MdDelete />
                  </Button>
                </Flex>
                );
               })}
          </Flex>
        </div>
      </div>

    </Flex>
  );
}
